import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Form, Button, Row, Col, Card } from 'react-bootstrap';
import { FiSearch, FiGrid, FiX, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import menuItems from '../../../../menu-items';

// Helper recursive function to extract all navigable items from menuItems
const flattenMenuItems = (items, parentTitle = '') => {
    let result = [];
    if (!items || !Array.isArray(items)) return result;

    items.forEach(item => {
        const currentGroup = parentTitle ? `${parentTitle}` : item.title;
        if (item.type === 'item' && item.url) {
            result.push({
                id: item.id || item.url,
                title: item.title,
                group: parentTitle || 'General',
                fullPath: parentTitle ? `${parentTitle} › ${item.title}` : item.title,
                url: item.url,
                icon: item.icon || 'bi bi-circle-fill'
            });
        }
        if (item.children && item.children.length > 0) {
            result = result.concat(flattenMenuItems(item.children, currentGroup));
        }
    });
    return result;
};

const HeaderSearchSitemap = () => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [showSitemapModal, setShowSitemapModal] = useState(false);
    const [sitemapFilter, setSitemapFilter] = useState('');
    const dropdownRef = useRef(null);

    const allPages = flattenMenuItems(menuItems.items);

    // Filter pages based on search query
    const filteredPages = query.trim() === ''
        ? []
        : allPages.filter(page =>
            page.title.toLowerCase().includes(query.toLowerCase()) ||
            page.group.toLowerCase().includes(query.toLowerCase()) ||
            page.fullPath.toLowerCase().includes(query.toLowerCase())
        );

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNavigate = (url) => {
        setIsOpen(false);
        setQuery('');
        setShowSitemapModal(false);
        navigate(url);
    };

    // Group items for Sitemap Modal
    const menuGroups = menuItems.items.map(group => {
        const pages = flattenMenuItems([group]);
        return {
            title: group.title,
            icon: group.icon,
            pages
        };
    });

    return (
        <div className="d-flex align-items-center" ref={dropdownRef} style={{ gap: '10px', position: 'relative' }}>
            {/* Header Search Input */}
            <div className="position-relative" style={{ width: '260px' }}>
                <Form.Control
                    type="text"
                    placeholder="Cercar a la plataforma..."
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    className="has-icon-left"
                    style={{
                        height: '36px',
                        paddingLeft: '44px',
                        paddingRight: query ? '32px' : '14px',
                        borderRadius: '9999px',
                        border: '1.5px solid #e2e5ea',
                        fontSize: '0.84rem',
                        backgroundColor: '#ffffff',
                        boxShadow: isOpen ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
                        transition: 'all 0.15s ease'
                    }}
                />
                <FiSearch
                    style={{
                        position: 'absolute',
                        left: '16px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af',
                        fontSize: '15px',
                        pointerEvents: 'none',
                        zIndex: 10
                    }}
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => { setQuery(''); setIsOpen(false); }}
                        style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            border: 'none',
                            background: 'transparent',
                            color: '#9ca3af',
                            cursor: 'pointer',
                            padding: 0,
                            zIndex: 10
                        }}
                    >
                        <FiX size={14} />
                    </button>
                )}
            </div>

            {/* Mapa del Lloc Button */}
            <Button
                type="button"
                variant="outline-secondary"
                className="d-flex align-items-center font-weight-bold shadow-sm"
                style={{
                    height: '36px',
                    borderRadius: '9999px',
                    fontSize: '0.82rem',
                    paddingLeft: '14px',
                    paddingRight: '14px',
                    borderColor: '#e2e5ea',
                    color: '#374151',
                    backgroundColor: '#ffffff',
                    whiteSpace: 'nowrap'
                }}
                onClick={() => setShowSitemapModal(true)}
                title="Veure Mapa del Lloc complet"
            >
                <FiGrid className="mr-1.5" size={15} style={{ color: 'var(--nexe-primary-red)' }} />
                <span>Mapa del Lloc</span>
            </Button>

            {/* Quick Search Overlay Dropdown */}
            {isOpen && query.trim().length > 0 && (
                <div
                    style={{
                        position: 'absolute',
                        top: '46px',
                        left: 0,
                        width: '420px',
                        maxHeight: '460px',
                        overflowY: 'auto',
                        backgroundColor: '#ffffff',
                        borderRadius: '14px',
                        boxShadow: '0 16px 36px rgba(0, 0, 0, 0.16), 0 4px 12px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e2e5ea',
                        zIndex: 1050,
                        padding: '0'
                    }}
                >
                    {/* Overlay Header */}
                    <div className="px-3 py-2.5 border-bottom d-flex align-items-center justify-content-between" style={{ backgroundColor: '#fafafa' }}>
                        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <FiSearch size={14} style={{ color: 'var(--nexe-primary-red)' }} />
                            <span className="small text-uppercase font-weight-bold text-dark" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                Resultats trobats ({filteredPages.length})
                            </span>
                        </div>
                    </div>

                    {/* Results Content */}
                    <div className="py-2">
                        {filteredPages.length === 0 ? (
                            <div className="px-4 py-4 text-center">
                                <div className="p-3 bg-light rounded-circle d-inline-flex mb-2 text-muted">
                                    <FiSearch size={22} />
                                </div>
                                <div className="text-dark font-weight-bold small">Sense resultats</div>
                                <div className="text-muted small">No s'ha trobat cap secció amb "<strong>{query}</strong>"</div>
                            </div>
                        ) : (
                            Object.entries(
                                filteredPages.reduce((acc, page) => {
                                    const grp = page.group || 'General';
                                    if (!acc[grp]) acc[grp] = [];
                                    acc[grp].push(page);
                                    return acc;
                                }, {})
                            ).map(([groupTitle, pages]) => (
                                <div key={groupTitle} className="mb-2">
                                    <div
                                        className="px-3 py-1.5 bg-light d-flex align-items-center justify-content-between border-top border-bottom"
                                        style={{ fontSize: '0.7rem', fontWeight: '700', color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.6px' }}
                                    >
                                        <span>{groupTitle}</span>
                                        <span className="badge badge-secondary rounded-pill" style={{ fontSize: '0.65rem', backgroundColor: '#e5e7eb', color: '#374151' }}>
                                            {pages.length}
                                        </span>
                                    </div>
                                    {pages.map((page, idx) => (
                                        <div
                                            key={idx}
                                            className="px-3.5 py-3 d-flex align-items-center justify-content-between"
                                            style={{
                                                cursor: 'pointer',
                                                paddingTop: '11px',
                                                paddingBottom: '11px',
                                                paddingLeft: '16px',
                                                paddingRight: '16px',
                                                transition: 'all 0.18s ease',
                                                borderLeft: '3px solid transparent',
                                                borderBottom: '1px solid #f3f4f6'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = '#fff5f5';
                                                e.currentTarget.style.borderLeftColor = '#e53935';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                                e.currentTarget.style.borderLeftColor = 'transparent';
                                            }}
                                            onClick={() => handleNavigate(page.url)}
                                        >
                                            <div className="d-flex align-items-center" style={{ gap: '12px', overflow: 'hidden' }}>
                                                <div
                                                    style={{
                                                        width: '32px',
                                                        height: '32px',
                                                        borderRadius: '8px',
                                                        background: 'linear-gradient(135deg, #e53935 0%, #e65100 100%)',
                                                        color: '#ffffff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        boxShadow: '0 2px 6px rgba(229, 57, 53, 0.25)'
                                                    }}
                                                >
                                                    <i className={page.icon} style={{ fontSize: '14px' }} />
                                                </div>
                                                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                    <div className="font-weight-bold text-dark text-truncate" style={{ fontSize: '0.86rem', lineHeight: '1.3', marginBottom: '3.5px' }}>
                                                        {page.title}
                                                    </div>
                                                    <div className="text-muted text-truncate" style={{ fontSize: '0.74rem', lineHeight: '1.25' }}>
                                                        {page.fullPath}
                                                    </div>
                                                </div>
                                            </div>
                                            <FiArrowRight size={15} className="text-muted flex-shrink-0 ml-2" />
                                        </div>
                                    ))}
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    <div
                        className="px-3 py-2.5 text-center border-top d-flex align-items-center justify-content-center"
                        style={{ cursor: 'pointer', backgroundColor: '#fafafa', gap: '6px' }}
                        onClick={() => { setIsOpen(false); setShowSitemapModal(true); }}
                    >
                        <span className="small text-danger font-weight-bold" style={{ fontSize: '0.82rem' }}>
                            Explorar tot el Mapa del Lloc
                        </span>
                        <FiArrowRight size={14} className="text-danger" />
                    </div>
                </div>
            )}

            {/* Sitemap Modal */}
            <Modal
                show={showSitemapModal}
                onHide={() => setShowSitemapModal(false)}
                size="xl"
                centered
                scrollable
            >
                <Modal.Header closeButton className="border-bottom py-3">
                    <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: '#fef2f2',
                                color: 'var(--nexe-primary-red)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <FiGrid size={20} />
                        </div>
                        <div>
                            <Modal.Title className="h5 font-weight-bold text-dark mb-0">
                                Mapa del Lloc - Gestió FCF
                            </Modal.Title>
                            <small className="text-muted">
                                Directori complet i interactiu de totes les seccions i eines de la plataforma
                            </small>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 bg-light">
                    {/* Modal Filter input */}
                    <div className="mb-4 bg-white p-3 rounded-lg shadow-sm border">
                        <div className="position-relative">
                            <Form.Control
                                type="text"
                                placeholder="Filtrar seccions del mapa del lloc..."
                                value={sitemapFilter}
                                onChange={(e) => setSitemapFilter(e.target.value)}
                                className="has-icon-left"
                                style={{
                                    height: '40px',
                                    paddingLeft: '44px',
                                    borderRadius: '9999px',
                                    border: '1.5px solid #e2e5ea',
                                    fontSize: '0.88rem'
                                }}
                            />
                            <FiSearch
                                style={{
                                    position: 'absolute',
                                    left: '16px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    fontSize: '15px'
                                }}
                            />
                            {sitemapFilter && (
                                <button
                                    type="button"
                                    onClick={() => setSitemapFilter('')}
                                    style={{
                                        position: 'absolute',
                                        right: '14px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        border: 'none',
                                        background: 'transparent',
                                        color: '#9ca3af',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <FiX size={15} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Sitemap Grid */}
                    <Row>
                        {menuGroups.map((group, gIdx) => {
                            const pagesMatch = group.pages.filter(p =>
                                !sitemapFilter ||
                                p.title.toLowerCase().includes(sitemapFilter.toLowerCase()) ||
                                group.title.toLowerCase().includes(sitemapFilter.toLowerCase())
                            );

                            if (pagesMatch.length === 0) return null;

                            return (
                                <Col md={6} lg={4} className="mb-4" key={gIdx}>
                                    <Card className="h-100 shadow-sm border-0 rounded-lg overflow-hidden">
                                        <Card.Header
                                            className="bg-white border-bottom py-3 d-flex align-items-center"
                                            style={{ gap: '10px' }}
                                        >
                                            <div
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#fef2f2',
                                                    color: 'var(--nexe-primary-red)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <i className={group.icon || 'bi bi-folder-fill'} style={{ fontSize: '15px' }} />
                                            </div>
                                            <h6 className="font-weight-bold text-dark mb-0" style={{ fontSize: '0.95rem' }}>
                                                {group.title}
                                            </h6>
                                        </Card.Header>
                                        <Card.Body className="p-2">
                                            <div className="list-group list-group-flush">
                                                {pagesMatch.map((p, pIdx) => (
                                                    <div
                                                        key={pIdx}
                                                        className="list-group-item list-group-item-action border-0 rounded-lg py-2 px-3 d-flex align-items-center justify-content-between mb-1"
                                                        style={{ cursor: 'pointer', transition: 'background-color 0.15s ease' }}
                                                        onClick={() => handleNavigate(p.url)}
                                                    >
                                                        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                                                            <FiCheckCircle size={14} style={{ color: 'var(--nexe-primary-red)', flexShrink: 0 }} />
                                                            <span className="font-weight-medium text-dark" style={{ fontSize: '0.85rem' }}>
                                                                {p.title}
                                                            </span>
                                                        </div>
                                                        <FiArrowRight size={13} className="text-muted" />
                                                    </div>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-top bg-white py-3 justify-content-between">
                    <span className="small text-muted">
                        Total seccions: <strong>{allPages.length}</strong> pàgines disponibles
                    </span>
                    <Button
                        variant="secondary"
                        className="px-4 rounded-pill font-weight-bold"
                        style={{ height: '36px', fontSize: '0.84rem' }}
                        onClick={() => setShowSitemapModal(false)}
                    >
                        Tancar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default HeaderSearchSitemap;
